import { GameState } from '../types/GameState';

type AlertVolume = 'low' | 'medium' | 'high';

interface AlertSettings {
  turnSound?: boolean;
  turnVolume?: AlertVolume;
  turnTone?: string;
  chatNotifications?: boolean;
  messageSound?: boolean;
  messageTone?: string;
  silenceMode?: boolean;
  silenceStart?: string;
  silenceEnd?: string;
  vibration?: boolean;
  vibrationIntensity?: 'soft' | 'medium' | 'strong';
}

const TONES: Record<string, { freq: number; type: OscillatorType; duration: number; double?: boolean }> = {
  bubble: { freq: 880, type: 'sine', duration: 0.1 },
  crystal: { freq: 1200, type: 'triangle', duration: 0.05 },
  echo: { freq: 660, type: 'sine', duration: 0.2, double: true },
  alert: { freq: 440, type: 'square', duration: 0.15 },
  classic: { freq: 800, type: 'sine', duration: 0.1 },
};

const DEFAULT_SETTINGS: AlertSettings = {
  turnSound: true,
  turnVolume: 'medium',
  turnTone: 'bubble',
  chatNotifications: true,
  messageSound: true,
  messageTone: 'crystal',
  silenceMode: false,
  silenceStart: '22:00',
  silenceEnd: '08:00',
  vibration: true,
  vibrationIntensity: 'medium',
};

export const getHumanTurnToken = (game: GameState) => (
  `${game.id}:${game.turnCount || 0}:${game.lastMove?.id || ''}:${game.updatedAt || ''}`
);

export const isHumanTurnGame = (game: GameState, userId: string) => (
  game.status === 'active' &&
  game.currentTurn === userId &&
  game.players.some((playerId) => playerId !== userId && playerId !== 'ROBOT') &&
  Boolean(game.lastMove?.userId && game.lastMove.userId !== userId && game.lastMove.userId !== 'ROBOT')
);

const parseTimeMinutes = (value = '00:00') => {
  const [hours, minutes] = value.split(':').map(Number);
  return (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0);
};

const isSilencedNow = (settings: AlertSettings) => {
  if (!settings.silenceMode) return false;

  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();
  const start = parseTimeMinutes(settings.silenceStart);
  const end = parseTimeMinutes(settings.silenceEnd);

  if (start === end) return true;
  if (start < end) return current >= start && current < end;
  return current >= start || current < end;
};

export const getAlertSettings = (): AlertSettings => {
  try {
    const saved = localStorage.getItem('game_alerts_settings');
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const playTone = (toneId: string, volumeLevel: AlertVolume, allowVibration: boolean) => {
  const settings = getAlertSettings();
  if (isSilencedNow(settings)) return;

  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    const audioCtx = new AudioContextClass();
    const tone = TONES[toneId] || TONES.bubble;
    const volumeMap: Record<AlertVolume, number> = { low: 0.05, medium: 0.15, high: 0.3 };
    const volume = volumeMap[volumeLevel || 'medium'];

    const playNote = (timeOffset = 0) => {
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      gainNode.gain.setValueAtTime(volume, audioCtx.currentTime + timeOffset);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + timeOffset + tone.duration);

      oscillator.type = tone.type;
      oscillator.frequency.setValueAtTime(tone.freq, audioCtx.currentTime + timeOffset);

      oscillator.start(audioCtx.currentTime + timeOffset);
      oscillator.stop(audioCtx.currentTime + timeOffset + tone.duration);
    };

    playNote();
    if (tone.double) playNote(0.15);

    if (allowVibration && settings.vibration && 'vibrate' in navigator) {
      const vibrationMap = { soft: 50, medium: 150, strong: 300 };
      navigator.vibrate(vibrationMap[settings.vibrationIntensity || 'medium']);
    }
  } catch {
    // Some browsers block audio until the user has interacted with the page.
  }
};

export const playTurnNotificationSound = () => {
  const settings = getAlertSettings();
  if (!settings.turnSound) return;
  playTone(settings.turnTone || 'bubble', settings.turnVolume || 'medium', true);
};

export const playMessageNotificationSound = () => {
  const settings = getAlertSettings();
  if (!settings.chatNotifications || !settings.messageSound) return;
  playTone(settings.messageTone || 'crystal', settings.turnVolume || 'medium', true);
};
